import { useState,useMemo, useEffect, useRef, useCallback } from "react";
import PipelineHeader from "./PipelineHeader";
import PipelineChart from "./PipelineChart";
import PipelineMetrics from "./PipelineMetrics";
import PipelineFooter from "./PipelineFooter";
import { useModuleControls } from "../context/ModuleControlsContext";
import { useInvert, useMetric  } from "../context/ModuleControlsContext";
import { useTooltip } from "../context/TooltipContext";
import { useTooltipMode } from "../context/TooltipModeContext";
import { getStatusMessage } from "../../../lib/statusMessages";

import "./Pipeline.css";

const ALARM_ROTATION_MS = 4000;
const BLOCKED_INJECTION_MESSAGE = "Inyección de o2 bloqueada, Sin presión en la línea";

function getPipelineStorageKey(moduloId, canId) {
  return `nanoox.pipeline-order.${String(moduloId ?? "default")}.${String(canId ?? "default")}`;
}

function getBombaKey(bomba, index) {
  return String(bomba?.bomba_id ?? bomba?.alias ?? bomba?.pipeline ?? `bomba-${index}`);
}


function sortBombasByAlias(bombas = []) {
  return [...bombas].sort((a, b) => Number(a.alias) - Number(b.alias));
}

function loadStoredPipelineOrder(storageKey) {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function persistPipelineOrder(storageKey, orderedKeys) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(orderedKeys));
  } catch (error) {
    // ignore persistence errors
  }
}

function normalizeBombasOrder(bombas = [], storedOrder = []) {
  const fallbackBombas = sortBombasByAlias(bombas);
  const bombaMap = new Map(fallbackBombas.map((bomba, index) => [getBombaKey(bomba, index), bomba]));
  const nextBombas = [];

  storedOrder.forEach((bombaKey) => {
    const bomba = bombaMap.get(bombaKey);
    if (!bomba) {
      return;
    }

    nextBombas.push(bomba);
    bombaMap.delete(bombaKey);
  });

  bombaMap.forEach((bomba) => {
    nextBombas.push(bomba);
  });

  return nextBombas;
}

const isValidNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value) && value >= 0;


function getVisibleO2Value(bomba) {
  if (isValidNumber(bomba?.O2) && bomba?.Measurement_validity) {
    return bomba.O2;
  }

  if (isValidNumber(bomba?.O2_bckp)) {
    return bomba.O2_bckp;
  }

  return null;
}

export default function Pipeline({ 
  translate,cansLenght, bombas, idxCan, isSingleModule, generador, controlType , statusGenerator,userLevel, selectedCenter,
  pressureHysteresis = false, suspendHeavyContent = false, dragDropEnabled = false, modulo_id, 
}) {

  // const { invertBombas } = useModuleControls();
  const { invertBombas } = useInvert();
  const pipelineStorageKey = useMemo(() => getPipelineStorageKey(modulo_id, idxCan), [idxCan, modulo_id]);
  const canEditLayout = dragDropEnabled;
  const [orderedBombas, setOrderedBombas] = useState([]);
  const [draggedBombaKey, setDraggedBombaKey] = useState(null);
  const [dragOverBombaKey, setDragOverBombaKey] = useState(null);
  
  // if (!bombas?.length) return <p>No hay bombas disponibles.</p>;

  useEffect(() => {
    setOrderedBombas(normalizeBombasOrder(bombas, loadStoredPipelineOrder(pipelineStorageKey)));
  }, [bombas, pipelineStorageKey]);

  const bombasOrdenadas = useMemo(() => orderedBombas, [orderedBombas]);

  let bombasArriba = bombasOrdenadas.filter((_, idx) =>
    invertBombas ? idx % 2 !== 0 : idx % 2 === 0
  );

  let bombasAbajo = bombasOrdenadas.filter((_, idx) =>
    invertBombas ? idx % 2 === 0 : idx % 2 !== 0
  );

  const pipelineLarge = isSingleModule && cansLenght < 4 

  const isDraggingLayout = suspendHeavyContent || draggedBombaKey !== null;

  const moveBomba = useCallback(
    (sourceKey, targetKey) => {
      if (!canEditLayout || !sourceKey || !targetKey || sourceKey === targetKey) {
        return;
      }

      setOrderedBombas((currentBombas) => {
        const nextBombas = [...currentBombas];
        const sourceIndex = nextBombas.findIndex((bomba, index) => getBombaKey(bomba, index) === sourceKey);
        const targetIndex = nextBombas.findIndex((bomba, index) => getBombaKey(bomba, index) === targetKey);

        if (sourceIndex < 0 || targetIndex < 0) {
          return currentBombas;
        }

        const [movedBomba] = nextBombas.splice(sourceIndex, 1);
        nextBombas.splice(targetIndex, 0, movedBomba);
        persistPipelineOrder(
          pipelineStorageKey,
          nextBombas.map((bomba, index) => getBombaKey(bomba, index))
        );
        return nextBombas;
      });
    },
    [canEditLayout, pipelineStorageKey]
  );

  const buildBombaDragProps = useCallback(
    (bomba) => {
      const bombaKey = getBombaKey(bomba);

      return {
        draggable: canEditLayout,
        className: `${draggedBombaKey === bombaKey ? "is-dragging" : ""} ${dragOverBombaKey === bombaKey ? "is-drag-target" : ""}`.trim(),
        onDragStart: () => setDraggedBombaKey(bombaKey),
        onDragEnd: () => {
          setDraggedBombaKey(null);
          setDragOverBombaKey(null);
        },
        onDragOver: (event) => {
          if (!canEditLayout) {
            return;
          }

          event.preventDefault();
          setDragOverBombaKey(bombaKey);
        },
        onDrop: (event) => {
          if (!canEditLayout) {
            return;
          }

          event.preventDefault();
          moveBomba(draggedBombaKey, bombaKey);
          setDraggedBombaKey(null);
          setDragOverBombaKey(null);
        },
      };
    },
    [canEditLayout, dragOverBombaKey, draggedBombaKey, moveBomba]
  );

  const propsBomba = {
    pipelineLarge, userLevel, statusGenerator, translate, idxCan, generador, controlType, isSingleModule, selectedCenter,modulo_id,
    pressureHysteresis, suspendHeavyContent:isDraggingLayout
  }

  return (
    <div className={`pipeline-container ${isSingleModule && cansLenght < 4 ? "pipeline-single" : "pipeline-multi"}`} >
      {/* Fila de pares */}
      <div  className={`pipeline-row ${bombasArriba.length === 1 ? "single-bomba" : ""}`}  // className="pipeline-row"
      >
        {bombasArriba.map((bomba, id) => (
          <BombaCard key={bomba.bomba_id} cantidad_bombas={bombasArriba.length} idx_bomba={id} bomba={bomba} interactionProps={buildBombaDragProps(bomba)} {...propsBomba} />
        ))}
      </div>

      {/* Fila de impares */}
      <div className={`pipeline-row ${bombasAbajo.length === 1 ? "single-bomba" : ""}`}// className="pipeline-row"
      >
        {bombasAbajo.map((bomba, id) => (
          <BombaCard key={bomba.bomba_id} cantidad_bombas={bombasAbajo.length} idx_bomba={id} bomba={bomba} interactionProps={buildBombaDragProps(bomba)}  {...propsBomba}  />
        ))}
      </div>
    </div>
  );
}

function BombaCard({ bomba,pipelineLarge, generador, controlType, isSingleModule, userLevel, modulo_id, idxCan, statusGenerator, cantidad_bombas, translate, selectedCenter, pressureHysteresis, interactionProps = null, suspendHeavyContent = false }) {
  // const { globalMetric } = useModuleControls();
  const { globalMetric } = useMetric();
  const { tooltipIdx } = useTooltip();        // tooltip global
  const { mode } = useTooltipMode();            //  modo (local o detailed)

  // métrica local (pero siempre sincronizada con global)
  const [metric, setMetric] = useState(globalMetric);
  const [alarmIndex, setAlarmIndex] = useState(0);
  const [tooltip, setTooltip] = useState({});
  const [controlOpen, setControlOpen] = useState(false);
  const injecting = bomba?.Inyecting_O2 ?? false;

  const tieneDatosCompletos = selectedCenter?.centro !== 'hueihue';


  const curita = true
  const StatusBomba =   bomba?.Start ?? false
  const StatusValvula = bomba?.Start_Valv ?? false

  const [attached, setAttached] = useState(false); // se pegó o no
  const [hovered, setHovered] = useState(false); // hover 3D
  const [floatUp, setFloatUp] = useState(false); // movimiento sutil


  // Animación de "se pega" al montar
  useEffect(() => {
    const t = setTimeout(() => setAttached(true), 50);
    return () => clearTimeout(t);
  }, []);


  // Movimiento sutil (flotando)
  // useEffect(() => {
  //   const id = setInterval(() => {
  //   setFloatUp(prev => !prev);
  //   }, 900);
  //   return () => clearInterval(id);
  // }, []);


    // Construir datos activos desde idx
  const activeIdx = mode === "detailed" ? tooltipIdx : tooltip?.idx;

  const tooltipData = useMemo(() => {
    if (mode === "detailed") {
      // Global sincronizado por índice
      if (activeIdx == null || !bomba.latest_measurements?.TS) return null;

      return {
        O2: bomba.latest_measurements.O2?.[activeIdx],
        SO2: bomba.latest_measurements.SO2?.[activeIdx],
        Temp: bomba.latest_measurements.Temp?.[activeIdx],
        TS: bomba.latest_measurements.TS?.[activeIdx],
      };
    } else {
      // Local → PipelineChart ya guarda valores completos en setTooltip
      return tooltip;
    }
  }, [mode, activeIdx, tooltip, bomba]);

  // cada vez que cambia la global, sobreescribir local
  useEffect(() => {
    setMetric(globalMetric);
  }, [globalMetric]);

  // const activeTooltip = mode === "detailed" ? tooltipIndex : tooltip;

  const handleToggleMetric = () => {
    if (suspendHeavyContent) {
      return;
    }

    // Solo cambia local hasta que la global vuelva a cambiar
    setMetric((prev) =>
      prev === "O2" ? "SO2" : prev === "SO2" ? "Temp" : "O2"
    );
  };

  // console.log('bomba',bomba)
  // === Estados derivados ===
  const {
    pipelineBg,
    pipelineTooltipBg,
    fallaSensor,
    bombaConectada,
    paradaEmergencia,
    modeAuto,
  } = useMemo(() => {
    const modeAuto = Boolean(bomba.InSel) ;

    return {
      // pipelineBg: !aviotStatus || !modeAuto
        // ? "linear-gradient(to left, rgb(34, 34, 34), rgb(68, 66, 71))"
        // : "linear-gradient(to left, rgb(20, 30, 48), rgb(36, 59, 85))",
      pipelineBg: bomba.O2_bckp === -1
        ? "linear-gradient(to left, rgba(39, 39, 39, 1), rgba(68, 68, 68, 1))" // 🎨 Null -> gris medio
        : !modeAuto
        // ? "radial-gradient(140% 120% at 100% 0%, rgba(180, 180, 180, 0.06) 0%, rgba(0, 0, 0, 0) 55%), linear-gradient(180deg, rgba(37, 38, 42, 1) 0%, rgba(26, 27, 30, 1) 100%)"   // ⚫ 
        ? "linear-gradient(to left, rgb(34, 34, 34), rgb(68, 66, 71))"    // ⚫ 
        // ? "linear-gradient(to bottom, #1a1a20, #0e0e12)"
        // : "linear-gradient(to left, rgb(20, 30, 48), rgb(36, 59, 85))",   // 🔵 
        : "linear-gradient(145deg, rgb(18, 25, 38) 0%, rgb(28, 45, 68) 100%)",   // 🔵 

        // : "radial-gradient(150% 130% at 110% 0%, rgba(56,189,248,0.10) 0%, rgba(0,0,0,0) 55%), linear-gradient(180deg, rgba(16, 32, 50, 1) 0%, rgba(11, 20, 36, 1) 100%)",


      pipelineTooltipBg: !modeAuto ? "pipeline" : "pipelineGray",
      fallaSensor:
        bomba.O2 <= 0 || bomba.O2 === null || (!bomba.O2_Modbus_Status),
      bombaConectada: bomba.Device_Modbus_Status,
      paradaEmergencia: Boolean(Number(bomba.InStop)),
      modeAuto,
    };
  }, [bomba]);

  // console.log('bomba', bomba)

  // === Rotación de alarmas ===
  useEffect(() => {
    if (controlType !== "PS" || !Array.isArray(bomba.alarms) || bomba.alarms.length <= 0) {
      return;
    }
    const interval = setInterval(
      () => setAlarmIndex((prev) => (prev + 1) % bomba.alarms.length),
      ALARM_ROTATION_MS
    );
    return () => clearInterval(interval);
  }, [controlType, bomba.alarms]);


  // === Mensaje de estado ===
  const statusMessage = useMemo(
    () =>
      getStatusMessage({
        generador,
        controlType,
        bomba,
        bombaConectada,
        paradaEmergencia,
        alarmIndex,
        translate,
      }),
    [generador, controlType, bomba, bombaConectada, paradaEmergencia, alarmIndex, translate]
  );

  const visibleO2 = getVisibleO2Value(bomba);
  const setPoint = Number(bomba?.SetPoint);
  const isBelowO2Threshold =
    isValidNumber(visibleO2) && Number.isFinite(setPoint) && visibleO2 < setPoint;

  const headerStatusMessage = useMemo(() => {
    if (injecting) {
      return statusMessage;
    }


    if (pressureHysteresis && isBelowO2Threshold) {
      return BLOCKED_INJECTION_MESSAGE;
    }

    return statusMessage;
  }, [bomba, injecting, pressureHysteresis, statusMessage]);
  

  // Preparamos datos para PipelineChart
  const data =
    bomba.latest_measurements?.TS?.map((ts, idx) => ({
      TS: ts,
      O2: bomba.latest_measurements.O2?.[idx],
      SO2: bomba.latest_measurements.SO2?.[idx],
      Temp: bomba.latest_measurements.Temp?.[idx],
      injecting: bomba.latest_measurements.Inyecting_O2?.[idx] ?? false,
      Mass_Flowmeter_Oxigeno: bomba.latest_measurements.Mass_Flowmeter_Oxigeno?.[idx],
      Acumulador_Oxigeno: bomba.latest_measurements.Acumulador_Oxigeno?.[idx],
      alarms: bomba.latest_measurements?.alarms?.[idx] ?? null,
    })) || [];

  return (
    // <div className={`bomba-card  ${(bomba.O2_bckp === -1 || bomba.O2_bckp === null ) ? "neon" : ""}`} style={{background:pipelineBg, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 26px rgba(0,0,0,0.35)",border: "0.1vw solid rgba(255,255,255,0.06)",}} onClick={handleToggleMetric}>
    <div className={`bomba-card ${(bomba.O2_bckp === -1 || bomba.O2_bckp === null ) ? "neon" : ""} ${interactionProps?.className ?? ""}`} style={{background:pipelineBg, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 26px rgba(0,0,0,0.35)",border: "0.1vw solid rgba(255,255,255,0.06)",}} onClick={handleToggleMetric} draggable={interactionProps?.draggable} onDragStart={interactionProps?.onDragStart} onDragEnd={interactionProps?.onDragEnd} onDragOver={interactionProps?.onDragOver} onDrop={interactionProps?.onDrop}>  
    {/* Fila 1: título */}

      {/* <ParcheCruzado
        attached={attached}
        hovered={hovered}
        floatUp={floatUp}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      /> */}

       {/* <CajaConBotiquinGame alias={bomba.alias}  fallaSensor={fallaSensor} pipelineTooltipBg={pipelineTooltipBg} /> */}

      <div className="bomba-row titulo">
        {/* <h3>{bomba.alias ?? bomba.nombre}</h3> */}
        <PipelineHeader
          bomba={bomba}
          userLevel={userLevel}
          modeAuto={modeAuto}
          statusMessage={statusMessage}
          pipelineTooltipBg={pipelineTooltipBg}
          fallaSensor={fallaSensor}
          onOpenControl={(e) =>{ setControlOpen(true); e.stopPropagation(); }}
          statusGenerator={statusGenerator}
          cantidad_bombas={cantidad_bombas}
          translate={translate}
          tieneDatosCompletos={tieneDatosCompletos}
          pressureHysteresis={pressureHysteresis}
          isBelowO2Threshold={isBelowO2Threshold}
          // curita={curita}
        />
      </div>

      {/* Fila 2: gráfico */}
      { tieneDatosCompletos &&
        <div className="bomba-row grafico" style={{marginTop: cantidad_bombas === 1 && '-0.5vh'}}>
          {suspendHeavyContent ? (
            <div className="pipeline-drag-placeholder">Reordenando...</div>
          ) : (
            <PipelineChart
              data={data}
              metric={metric}
              bomba={bomba}
              isSingleModule={isSingleModule}
              tempMin={3}    //  configurable
              tempMax={14}   // configurable
              fallaSensor={fallaSensor}
              tooltip={tooltip}
              setTooltip={setTooltip}
              pipelineLarge={pipelineLarge}
              cantidad_bombas={cantidad_bombas}
              translate={translate}
              userLevel={userLevel}
            />
          )}
        </div>
      }

      {!tieneDatosCompletos && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vw', padding:'3vh 1vw', fontSize:'min(1vw, 2vh)', color:'#c3c3c3',stroke: 'rgba(255, 255, 255, 0.1)', textShadow: '0 0 2px rgba(0, 0, 0, 1)' }}>
          <span> {bomba.Vo} V </span>
          <span> {bomba.Io} A </span>
        </div>
      )}


      {/* Fila 3: info */}
      { tieneDatosCompletos &&
        <div className="bomba-row info">
          {suspendHeavyContent ? null : (
            <PipelineMetrics
              bomba={bomba}
              tooltipSource={mode === "detailed" ? "global" : "local"}
              tooltip={tooltipData}
              mainMetric={metric}
              fallaSensor={fallaSensor}
              modeAuto={modeAuto}
              bombaConectada={bombaConectada}
              mode={mode}
              cantidad_bombas={cantidad_bombas}
              pipelineLarge={pipelineLarge}

            />
          )}
        </div>
      }

      {/* Overlay de burbujas */}
      {!suspendHeavyContent && injecting && <BubblesOverlay /> }

    </div>
  );
}

function BubblesOverlay({ isActive = true }) {

    const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;

    const TOTAL = 45;              // cantidad total de burbujas
    const LAYERS = 3;              // profundidad real
    const bubbles = [];

    function createBubble(layer) {
      const el = document.createElement("span");
      el.className = "bubble";

      const duration = 12 + Math.random() * 18;
      const delay = -(Math.random() * duration);

      const size = 0.4 + Math.random() * 0.50;
      const amplitude = 5 + Math.random() * (layer * 15); 
      const blur = layer * 1.5;

      el.style.left = `${Math.random() * 100}%`;
      el.style.width = `${size}vw`;
      el.style.height = `${size}vw`;

      el.style.animation = `bubbleFloat ${duration}s linear infinite`;
      el.style.animationDelay = `${delay}s`;
      el.style.setProperty("--amp", `${amplitude}px`);
      el.style.setProperty("--blur", `${blur}px`);
      el.style.zIndex = layer;

      bubbles.push(el);
      container.appendChild(el);
    }

    for (let i = 1; i <= LAYERS; i++) {
      for (let j = 0; j < TOTAL / LAYERS; j++) {
        createBubble(i);
      }
    }

    return () => {
      bubbles.forEach((b) => container.removeChild(b));
    };
  }, []);


  return (
    <>
      <div ref={containerRef} className="bubbles-layer" />

      <style>{`
        .bubbles-layer {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
        }

        .bubble {
          position: absolute;
          bottom: -50px;
          background: rgba(255, 255, 255, 0.35);
          border-radius: 50%;
          animation-fill-mode: both;
          filter: blur(0.4px);
        }

        @keyframes bubbleFloat {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          50% {
            transform: translateY(-50vh) translateX(var(--amp));
          }
          100% {
            transform: translateY(-105vh) translateX(calc(var(--amp) * -1));
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
}


//   const BURBUJA_VELOCIDADES = {
//     lento:  { min: 12, max: 18 }, // 12–18 segundos
//     medio:  { min: 8,  max: 12 }, // 8–12 segundos
//     rapido: { min: 4,  max: 7 }   // 4–7 segundos
//   };

//   const VELOCIDAD_ACTUAL = "lento";
//   const [bubbles, setBubbles] = useState([]);
//   const maxBubbles = 50;
//   const bubbleId = useRef(1);
//   const activeBubbles = useRef(0);

//   const addBubble = useCallback(() => {
//     if (activeBubbles.current >= maxBubbles) return;

//     const id = bubbleId.current++;
//     const left = Math.random() * 90;
//     const { min, max } = BURBUJA_VELOCIDADES[VELOCIDAD_ACTUAL];
//     const duration = min + Math.random() * (max - min);

//     setBubbles(prev => [...prev, { id, left, duration }]);
//     activeBubbles.current += 1;

//     // Eliminar burbuja cuando termina su animación
//     setTimeout(() => {
//       setBubbles(current => current.filter(b => b.id !== id));
//       activeBubbles.current -= 1;

//       // Agregar otra burbuja luego de un pequeño delay aleatorio
//       const delay = 500 + Math.random() * 800;
//       if (isActive) setTimeout(addBubble, delay);
//     }, duration * 1000);
//   }, [isActive]);

//   useEffect(() => {
//     if (isActive) {
//       if (activeBubbles.current === 0) {
//         // Crea burbujas iniciales con un pequeño retraso escalonado
//         for (let i = 0; i < maxBubbles; i++) {
//           setTimeout(() => addBubble(), i * 300); // espaciadas 300ms
//         }
//       }
//     } else {
//       // Limpieza completa si se desactiva
//       setBubbles([]);
//       activeBubbles.current = 0;
//       bubbleId.current = 1;
//     }
//   }, [isActive, addBubble]);

//   return (
//     <div className="bubbles-overlay">
//       {bubbles.map(bubble => (
//         <div
//           key={bubble.id}
//           className="bubble"
//           style={{
//             left: `${bubble.left}%`,
//             animation: `bubble-animation ${bubble.duration}s linear forwards`
//           }}
//         />
//       ))}
//     </div>
//   );
// }


